import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioEngine, SafeAudioEngine } from '@/lib/microtonal/audio';

/**
 * Hook providing access to the singleton SafeAudioEngine.
 * Includes a volume slider state and waveform selection.
 */
export function useAudioEngine() {
  const engineRef = useRef<SafeAudioEngine | null>(null);
  const [volume, setVolumeState] = useState(0.5);
  const [waveform, setWaveformState] = useState<'sine' | 'triangle' | 'square' | 'sawtooth'>('sine');
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set());

  if (!engineRef.current) {
    engineRef.current = getAudioEngine();
  }

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    engineRef.current!.setVolume(v);
  }, []);

  const setWaveform = useCallback((w: 'sine' | 'triangle' | 'square' | 'sawtooth') => {
    setWaveformState(w);
    engineRef.current!.setWaveform(w);
  }, []);

  const noteOn = useCallback((keyId: string, frequency: number, velocity = 0.7) => {
    engineRef.current!.noteOn(keyId, frequency, velocity);
    setActiveKeys((prev) => new Set(prev).add(keyId));
  }, []);

  const noteOff = useCallback((keyId: string) => {
    engineRef.current!.noteOff(keyId);
    setActiveKeys((prev) => {
      const next = new Set(prev);
      next.delete(keyId);
      return next;
    });
  }, []);

  const allNotesOff = useCallback(() => {
    engineRef.current!.allNotesOff();
    setActiveKeys(new Set());
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      engineRef.current?.allNotesOff();
    };
  }, []);

  return {
    engine: engineRef.current,
    volume,
    setVolume,
    waveform,
    setWaveform,
    noteOn,
    noteOff,
    allNotesOff,
    activeKeys,
  };
}
